import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BrandIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Calculate as CalculateIcon,
  AutoStories as StoriesIcon,
  Mail as MailIcon,
  MenuBook as MenuBookIcon,
  BarChart as BarChartIcon,
  Home as HomeIcon,
  Science as ScienceIcon,
  LocalLibrary as LibraryIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../hooks/useAuth';
import NotificationsSystem from '../dashboard/NotificationsSystem';
import SearchSystem from '../dashboard/SearchSystem';
import ThemeModeToggle from '../common/ThemeModeToggle';
import '../dashboard/ReportListing.css';
import '../../styles/dashboard-shell.css';
import { getReports } from '../../services/reportService';
import { getBrands } from '../../services/brandService';
import { BRAND } from '../../theme/brandTheme';
import { isStakeholder } from '../../utils/roleUtils';
import VisibilityIcon from '@mui/icons-material/Visibility';

const drawerWidth = 280;

const DashboardLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [allReports, setAllReports] = useState([]);
  const [sidebarBrands, setSidebarBrands] = useState([]);
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const handleProfileMenuOpen = (event) => setProfileMenuAnchor(event.currentTarget);
  const handleProfileMenuClose = () => setProfileMenuAnchor(null);

  const handleProfile = () => {
    handleProfileMenuClose();
    navigate('/dashboard/profile');
  };

  const menuItems = [
    { text: 'Portföy Özeti', icon: <VisibilityIcon />, path: '/dashboard/stakeholder', roles: ['stakeholder'] },
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', roles: ['admin', 'editor', 'user'] },
    { text: 'İstatistikler', icon: <BarChartIcon />, path: '/dashboard/statistics', roles: ['admin', 'editor', 'user', 'stakeholder'] },
    { text: 'Test Kütüphanesi', icon: <LibraryIcon />, path: '/dashboard/library', roles: ['admin', 'editor', 'user'] },
    { text: 'AB Test Hesaplayıcı', icon: <CalculateIcon />, path: '/dashboard/calculator', roles: ['admin', 'editor', 'user'] },
    { text: 'Yöntemler Rehberi', icon: <ScienceIcon />, path: '/methods-guide', roles: ['admin', 'editor', 'user', 'stakeholder'] },
    { text: 'Metrik Rehberi', icon: <MenuBookIcon />, path: '/calculation-guide', roles: ['admin', 'editor', 'user', 'stakeholder'] },
    { text: 'Anasayfa', icon: <HomeIcon />, path: '/', roles: ['admin', 'editor', 'user', 'stakeholder'], externalApp: true },
    { text: 'Kullanıcı Yönetimi', icon: <PeopleIcon />, path: '/dashboard/users', roles: ['admin'] },
    { text: 'Marka Yönetimi', icon: <BrandIcon />, path: '/dashboard/brands', roles: ['admin'] },
    { text: 'Başarı Hikayeleri', icon: <StoriesIcon />, path: '/dashboard/success-stories', roles: ['admin'] },
    { text: 'Form Gönderimleri', icon: <MailIcon />, path: '/dashboard/form-submissions', roles: ['admin'] },
  ];

  const filteredMenuItems = menuItems.filter((item) =>
    item.roles.includes(user?.role || 'user'),
  );

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const brandIds =
          user?.role === 'admin' ? [] : user?.brands?.map((brand) => brand._id) || [];
        const reportsData = await getReports(brandIds);
        const flatReports = reportsData.reduce((acc, brand) => {
          return [...acc, ...brand.tests.map((test) => ({ ...test, brand: brand.name }))];
        }, []);
        setAllReports(flatReports);
      } catch (error) {
        console.error('Error fetching reports:', error);
      }
    };

    if (user) fetchReports();
  }, [user]);

  // Sidebar brand links — user's assigned brands or all for admin/editor
  useEffect(() => {
    const loadBrands = async () => {
      if (!user) return;
      try {
        if (user.role === 'admin' || user.role === 'editor') {
          const brands = await getBrands();
          setSidebarBrands(brands || []);
        } else {
          setSidebarBrands(user.brands || []);
        }
      } catch (err) {
        console.error('Error fetching sidebar brands:', err);
      }
    };
    loadBrands();
  }, [user]);

  const drawer = (
    <div className="sidebar">
      <div className="sidebar-header">
        <RouterLink
          to={isStakeholder(user) ? '/dashboard/stakeholder' : '/dashboard'}
          className="sidebar-logo-link"
        >
          <Box
            component="img"
            src={BRAND.logoWhite}
            alt="Hype"
            className="sidebar-logo"
          />
        </RouterLink>
      </div>

      <div className="sidebar-content">
        <Typography component="span" className="sidebar-section-label">
          MENÜ
        </Typography>
        <List sx={{ padding: '8px 0' }}>
          {filteredMenuItems.map((item) => {
            const isActive =
              !item.externalApp &&
              (location.pathname === item.path ||
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path)));

            return (
              <ListItem key={item.text} disablePadding className={isActive ? 'menu-item active' : 'menu-item'}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  className="menu-button"
                >
                  <ListItemIcon className="menu-icon">{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: 15,
                      fontWeight: isActive ? 600 : 500,
                      fontFamily: BRAND.fontFamily,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {sidebarBrands.length > 0 && (
          <>
            <Typography component="span" className="sidebar-section-label" sx={{ mt: 2 }}>
              MARKALAR
            </Typography>
            <List sx={{ padding: '8px 0' }}>
              {sidebarBrands.map((brand) => {
                const brandPath = `/dashboard/brand/${brand._id}`;
                const isBrandActive = location.pathname === brandPath;

                return (
                  <ListItem
                    key={brand._id}
                    disablePadding
                    className={isBrandActive ? 'menu-item active' : 'menu-item'}
                  >
                    <ListItemButton onClick={() => navigate(brandPath)} className="menu-button">
                      <ListItemIcon className="menu-icon">
                        {brand.logo ? (
                          <Box
                            component="img"
                            src={brand.logo}
                            alt=""
                            sx={{ width: 22, height: 22, objectFit: 'contain' }}
                          />
                        ) : (
                          <BrandIcon sx={{ fontSize: 22 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={brand.name}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: isBrandActive ? 600 : 500,
                          fontFamily: BRAND.fontFamily,
                          noWrap: true,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </div>

      <div className="sidebar-footer">
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', pl: 2 }}>
          <Avatar sx={{ width: 40, height: 40, mr: 1.5 }}>
            {user?.username?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}>
              {user?.username || 'User'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', cursor: 'pointer' }}
              onClick={handleLogout}
            >
              çıkış →
            </Typography>
          </Box>
        </Box>
      </div>
    </div>
  );

  return (
    <Box className="dashboard-shell" sx={{ display: 'flex' }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        className="dashboard-topbar"
        elevation={0}
        sx={{
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          zIndex: 1200,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 64, md: 72 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ display: { md: 'none' }, color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
            <RouterLink to="/dashboard">
              <Box
                component="img"
                src={isDark ? BRAND.logoWhite : BRAND.logoDark}
                alt="Hype"
                className="topbar-logo"
                sx={{ display: { xs: 'block', md: 'none' }, height: 24 }}
              />
            </RouterLink>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' },
                fontFamily: BRAND.fontFamily,
              }}
            >
              CRO Dashboard
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ThemeModeToggle sx={{ color: 'text.primary' }} />
            <SearchSystem reports={allReports} />
            <NotificationsSystem />
            <IconButton onClick={handleProfileMenuOpen} sx={{ ml: 0.5 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: BRAND.purple }}>
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            anchorEl={profileMenuAnchor}
            open={Boolean(profileMenuAnchor)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 180,
                boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                border: `1px solid ${BRAND.borderLight}`,
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleProfile}>
              <PersonIcon sx={{ mr: 2, fontSize: 20 }} />
              Profil
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 2, fontSize: 20 }} />
              Çıkış Yap
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        className="dashboard-main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, md: 72 } }} />
        <Outlet />
      </Box>
    </Box>
  );
};

export default DashboardLayout;
